import React, { useState, useEffect, memo } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  ImageList,
  ImageListItem,
  useTheme,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  NavigateBefore,
  NavigateNext,
  Save as SaveIcon,
  LocationOn
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AddRoommateReq, AddRoommateImg } from '../../services/advert';
import { GetCountries, GetCities, GetDistrict } from '../../services/location';
import { DEFAULT_IMAGE } from '../../constants';
import GoogleMapReact from 'google-map-react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import BasicInformationStep from './steps/BasicInformationStep';
import DetailsStep from './steps/DetailsStep';
import ImagesStep from './steps/ImagesStep';

const Marker = () => <LocationOn sx={{ color: 'error.main', transform: 'translate(-50%, -100%)' }} />;

const INITIAL_FORM_STATE = {
  listingTitle: '',
  country: '',
  city: '',
  district: '',
  address: '',
  description: '',
  monthlyRent: '',
  utilitiesIncluded: false,
  availableFrom: new Date(),
  gender: '',
  preferredAgeRangeStart: '',
  preferredAgeRangeEnd: '',
  petsAllowed: false,
  smokingAllowed: false,
  alcoholAllowed: false,
  latitude: '',
  longitude: ''
};

const INITIAL_MAP_CENTER = {
  lat: 41.0082,
  lng: 28.9784
};

const AddRoommate = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [images, setImages] = useState([]);
  const [advertId, setAdvertId] = useState(null);
  const [mapCenter, setMapCenter] = useState(INITIAL_MAP_CENTER);
  const [locations, setLocations] = useState({
    countries: [],
    cities: [],
    districts: []
  });

  // Fetch initial data
  useEffect(() => {
    fetchCountries();
  }, []);

  // Fetch cities when country changes
  useEffect(() => {
    if (formData.country) {
      fetchCities(formData.country);
    }
  }, [formData.country]);

  // Fetch districts when city changes
  useEffect(() => {
    if (formData.city) {
      fetchDistricts(formData.city);
    }
  }, [formData.city]);

  // API calls
  const fetchCountries = async () => {
    try {
      const response = await GetCountries();
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          countries: response.data
        }));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const fetchCities = async (countryId) => {
    try {
      const response = await GetCities(countryId);
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          cities: response.data,
          districts: []
        }));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await GetDistrict(cityId);
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          districts: response.data
        }));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Form handlers
  const handleCountryChange = (event) => {
    const countryId = event.target.value;
    setFormData(prev => ({ ...prev, country: countryId, city: '', district: '' }));
  };

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    setFormData(prev => ({ ...prev, city: cityId, district: '' }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("RID", advertId);
    formData.append("FileName", file);

    try {
      const response = await AddRoommateImg(formData);
      if (response.statusCode === 200) {
        setImages(prev => [...prev, {
          id: response.data,
          url: URL.createObjectURL(file),
          type: 2
        }]);
        toast.success(t('common.imageUploaded'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Validation
  const validateFormData = () => {
    const requiredFields = [
      { key: 'listingTitle', message: t('addRoommate.validation.titleRequired') },
      { key: 'country', message: t('addRoommate.validation.countryRequired') },
      { key: 'city', message: t('addRoommate.validation.cityRequired') },
      { key: 'district', message: t('addRoommate.validation.districtRequired') },
      { key: 'address', message: t('addRoommate.validation.addressRequired') },
      { key: 'monthlyRent', message: t('addRoommate.validation.rentRequired') },
    ];

    for (const field of requiredFields) {
      if (!formData[field.key] || formData[field.key].toString().trim() === '') {
        toast.error(field.message);
        return false;
      }
    }

    return true;
  };

  // Step handlers
  const handleSubmit = async () => {
    if (!validateFormData()) return;

    try {
      setIsLoading(true);
      const requestData = {
        ...formData,
        latitute: formData.latitude,
        longtitute: formData.longitude
      };
      const response = await AddRoommateReq(requestData);
      
      if (response.statusCode === 200) {
        setAdvertId(response.data);
        toast.success(t('addRoommate.success'));
        setActiveStep(activeStep + 1);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = () => {
    if (images.length === 0) {
      toast.warning(t('addRoommate.noImages'));
      return;
    }
    navigate('/my-advertisements');
  };

  // Step components
  const steps = [
    t('addRoommate.steps.basic'),
    t('addRoommate.steps.details'),
    t('addRoommate.steps.images')
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('addRoommate.title')}
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 2 }}>
          {activeStep === 0 && (
            <BasicInformationStep
              formData={formData}
              setFormData={setFormData}
              locations={locations}
              handleCountryChange={handleCountryChange}
              handleCityChange={handleCityChange}
              mapCenter={mapCenter}
              setMapCenter={setMapCenter}
              t={t}
            />
          )}
          {activeStep === 1 && (
            <DetailsStep
              formData={formData}
              setFormData={setFormData}
              t={t}
            />
          )}
          {activeStep === 2 && (
            <ImagesStep
              handleImageUpload={handleImageUpload}
              images={images}
              t={t}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            onClick={() => setActiveStep((prev) => prev - 1)}
            disabled={activeStep === 0}
            startIcon={<NavigateBefore />}
          >
            {t('common.back')}
          </Button>

          <Button
            variant="contained"
            onClick={activeStep === 1 ? handleSubmit : activeStep === 2 ? handleFinish : () => setActiveStep(prev => prev + 1)}
            endIcon={activeStep === 2 ? <SaveIcon /> : <NavigateNext />}
          >
            {activeStep === 2 ? t('common.finish') : activeStep === 1 ? t('common.save') : t('common.next')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddRoommate; 